import { useEffect, useState } from "react";

import { teamAbbrev, teamFullName } from "../../../data/teams";
import {
  teamBranding,
  teamLogoSrc,
  teamLogoSrcSet,
  teamLogoVariantSrc,
} from "../../../data/teamBranding";
import styles from "./TeamLogo.module.css";

const RENDERED_PIXELS = Object.freeze({
  sm: 24,
  md: 36,
  lg: 48,
  xl: 64,
});

/**
 * A club's logo, with a lettered crest as the fallback.
 *
 * The fallback is not an error path, it is the design: if
 * `public/logos/nfl` is ever removed (see the licensing note in
 * scripts/fetch-team-logos.mjs) every logo in the product becomes a crest
 * in the club's colours and nothing else changes. The same path covers a
 * club we have no branding for and an image that fails to load.
 *
 * Decorative by default. Every place the product shows a logo, it shows
 * the abbreviation or the full name beside it, so announcing the logo too
 * would just make a screen reader say the team twice. Pass `labelled` for
 * the rare case where the logo stands alone.
 *
 * size: "sm" | "md" | "lg" | "xl"
 */
const TeamLogo = ({ teamId, size = "md", labelled = false, className = "" }) => {
  const src = teamLogoSrc(teamId);
  const [failed, setFailed] = useState(false);
  const renderedPixels = RENDERED_PIXELS[size] ?? RENDERED_PIXELS.md;

  /* A new club means a new image: give it a chance before falling back. */
  useEffect(() => {
    setFailed(false);
  }, [src]);

  const sizeClass = styles[`size_${size}`] ?? styles.size_md;
  const label = labelled ? `${teamFullName(teamId)} logo` : undefined;
  const a11y = labelled
    ? { role: "img", "aria-label": label }
    : { "aria-hidden": "true" };

  if (!src || failed) {
    const { primary, onPrimary } = teamBranding(teamId);
    return (
      <span
        className={`${styles.TeamLogo} ${sizeClass} ${styles.crest} ${className}`.trim()}
        style={{ background: primary, color: onPrimary }}
        {...a11y}
      >
        <span className={styles.crestText}>{teamAbbrev(teamId)}</span>
      </span>
    );
  }

  return (
    <span
      className={`${styles.TeamLogo} ${sizeClass} ${styles.plate} ${className}`.trim()}
      {...a11y}
    >
      <picture className={styles.picture}>
        <source
          type="image/webp"
          srcSet={teamLogoSrcSet(teamId, "webp")}
          sizes={`${renderedPixels}px`}
        />
        <source
          type="image/png"
          srcSet={teamLogoSrcSet(teamId, "png")}
          sizes={`${renderedPixels}px`}
        />
        <img
          className={styles.image}
          src={teamLogoVariantSrc(teamId, renderedPixels * 2, "png") ?? src}
          width={renderedPixels}
          height={renderedPixels}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      </picture>
    </span>
  );
};

export default TeamLogo;
